import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { createChart, CrosshairMode } from "lightweight-charts";
import { optionChartAPI, strategyAPI, backtestAPI } from "../services/api";
import { isOptionSymbol, parseOptionSymbol } from "../utils/optionSymbol";

const C = {
  card: "#fff", border: "#e8edf3", text: "#0f172a", sub: "#64748b",
  blue: "#2563eb", green: "#10b981", red: "#ef4444", bg: "#f8fafc",
};
const card = (extra = {}) => ({ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, ...extra });
const TFS = [{ id: "1minute", label: "1m" }, { id: "3minute", label: "3m" }, { id: "5minute", label: "5m" }, { id: "15minute", label: "15m" }, { id: "30minute", label: "30m" }, { id: "60minute", label: "60m" }];
const UNDERLYINGS = ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Build an expired-option trading symbol from its parts (mirrors backend buildSymbol):
// month 1-9 = digit, Oct/Nov/Dec = O/N/D.
const moCode = (mo) => (mo >= 10 ? ["O", "N", "D"][mo - 10] : String(mo));
const buildSym = (und, expiry, strike, opt) => {
  if (!und || !expiry || !strike || !opt) return "";
  const [y, m, d] = expiry.split("-");
  return `${und}${y.slice(2)}${moCode(Number(m))}${d}${strike}${opt}`;
};
const sel = (extra = {}) => ({ padding: "9px 10px", border: "1px solid #e8edf3", borderRadius: 8, fontSize: 14, background: "#fff", color: "#0f172a", cursor: "pointer", ...extra });
const TYPES = [
  { id: "candles", label: "Candles" }, { id: "heikin", label: "Heikin Ashi" },
  { id: "bars", label: "Bars" }, { id: "line", label: "Line" }, { id: "area", label: "Area" },
];

// Feed the IST wall-clock AS IF it were UTC: lightweight-charts renders timestamps in
// UTC, so this makes the axis show the real IST market time (09:15…15:30) for every
// viewer — the standard fixed-exchange-timezone trick (what Kite does).
const toTime = (c) => Math.floor(Date.parse(`${c.date}T${c.time}:00Z`) / 1000);

function heikinAshi(cs) {
  const out = []; let p = null;
  for (const c of cs) {
    const close = (c.o + c.h + c.l + c.c) / 4;
    const open = p ? (p.open + p.close) / 2 : (c.o + c.c) / 2;
    out.push({ time: c._t, open, high: Math.max(c.h, open, close), low: Math.min(c.l, open, close), close });
    p = out[out.length - 1];
  }
  return out;
}
function emaLine(cs, period) {
  const k = 2 / (period + 1); let e = null; const out = [];
  for (const c of cs) { e = e == null ? c.c : c.c * k + e * (1 - k); out.push({ time: c._t, value: e }); }
  return out;
}
function vwapLine(cs) {
  const out = []; let day = null, pv = 0, vv = 0;
  for (const c of cs) {
    if (c.date !== day) { day = c.date; pv = 0; vv = 0; }
    const tp = (c.h + c.l + c.c) / 3, v = c.volume || 0; pv += tp * v; vv += v;
    out.push({ time: c._t, value: vv ? pv / vv : tp });
  }
  return out;
}

const IB = "#7c3aed"; // inside-candle highlight (purple)
const TF_MIN = { "1min": 1, "5min": 5, "15min": 15, "30min": 30, "60min": 60, "1hour": 60, "2hour": 120, "4hour": 240 };

// Resample fine (≈1-min) candles to a higher timeframe the same way the engine does:
// bucket by floor(minuteOfDay / tfMin) within each day (09:15 sits on a 5/15-min
// boundary, so this matches NSE 5/15-min candle starts). Carries the source indices
// (_idx) so a TF inside-bar can be mapped back onto the displayed candles.
function resample(cs, tfMin) {
  if (tfMin <= 1) return cs.map((c, i) => ({ ...c, _idx: [i] }));
  const out = []; let b = null, key = null;
  cs.forEach((c, i) => {
    const [hh, mm] = c.time.split(":").map(Number);
    const k = c.date + "|" + Math.floor((hh * 60 + mm) / tfMin);
    if (k !== key) { if (b) out.push(b); key = k; b = { date: c.date, time: c.time, o: c.o, h: c.h, l: c.l, c: c.c, _idx: [i] }; }
    else { b.h = Math.max(b.h, c.h); b.l = Math.min(b.l, c.l); b.c = c.c; b._idx.push(i); }
  });
  if (b) out.push(b);
  return out;
}

// Inside bar = fully engulfed by the previous bar (high < prevHigh && low > prevLow), never
// across a day boundary. Optional quality filters mirror the strategy's insideBar config:
//   maxBabyPct    — baby range must be ≤ this % of the mother range (0 = off).
//   minConsecutive — only mark bars that are part of a run of ≥ N consecutive inside bars.
function detectInside(bars, { maxBabyPct = 0, minConsecutive = 1 } = {}) {
  const ins = new Array(bars.length).fill(false);
  for (let i = 1; i < bars.length; i++) {
    const c = bars[i], p = bars[i - 1];
    if (c.date !== p.date) continue;
    let hit = c.h < p.h && c.l > p.l;
    if (hit && maxBabyPct > 0) {
      const mother = p.h - p.l, baby = c.h - c.l;
      if (mother > 0 && baby > mother * (maxBabyPct / 100)) hit = false;
    }
    ins[i] = hit;
  }
  if (minConsecutive > 1) {
    const keep = new Array(bars.length).fill(false);
    let run = 0;
    for (let i = 0; i < bars.length; i++) {
      if (ins[i]) { run++; if (run >= minConsecutive) for (let j = i; j > i - run; j--) keep[j] = true; }
      else run = 0;
    }
    return keep;
  }
  return ins;
}

function Chart({ candles, toolbarLeft, deepLink }) {
  const elRef = useRef(null);
  const chartRef = useRef(null);
  const mainRef = useRef(null);
  const seriesRef = useRef([]);     // every series currently on the chart
  const prevDataRef = useRef(null);
  const [legend, setLegend] = useState(null);
  const [type, setType] = useState("candles");
  const [showVol, setShowVol] = useState(true);
  const [ind, setInd] = useState({ ema9: false, ema21: false, vwap: false });
  const [log, setLog] = useState(false);
  // Inside-bar overlay driven by a saved strategy's logic.
  const [ibStrats, setIbStrats] = useState([]);   // user's inside-bar strategies
  const [ibStratId, setIbStratId] = useState(""); // "" = off, "manual" = generic 1:1, else strategy _id
  const [ibDay, setIbDay] = useState("all");       // filter highlight/zoom to one day

  const cs = useMemo(() => candles.map((c) => ({ ...c, _t: toTime(c) })), [candles]);

  // Load the user's inside-bar strategies once (for the chart's strategy selector).
  // Also include a deep-linked strategy (from a backtest "View chart" link) even if
  // it isn't an inside-bar strategy, so its overlay/name resolve here.
  useEffect(() => {
    let alive = true;
    strategyAPI.getAll()
      .then((r) => {
        if (!alive) return;
        const all = r.data || [];
        const ib = all.filter((s) => s?.insideBar?.enabled);
        if (deepLink?.strategyId && !ib.some((s) => s._id === deepLink.strategyId)) {
          const dl = all.find((s) => s._id === deepLink.strategyId);
          if (dl) ib.push(dl);
        }
        setIbStrats(ib);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [deepLink]);

  // Deep link from a backtest "View chart": select the strategy + focus its day, and
  // auto-run the overlay once candles load. The inside-bar TINT is suppressed for a
  // non-inside-bar strategy (see ibSet memo), so only entry/exit (and SL/Target when
  // present) overlay — exactly the per-leg chart the crypto portal shows.
  const dlAppliedRef = useRef(false);
  useEffect(() => {
    if (dlAppliedRef.current || !deepLink) return;
    if (!deepLink.strategyId && !deepLink.date) return;
    dlAppliedRef.current = true;
    if (deepLink.strategyId) setIbStratId(deepLink.strategyId);
    // Focus the trade's day even on a strategy-less option deep link (so a per-leg chart
    // zooms straight to the trade day).
    if (deepLink.date) setIbDay(deepLink.date);
  }, [deepLink]);

  const days = useMemo(() => [...new Set(cs.map((c) => c.date))], [cs]);
  useEffect(() => { if (ibDay !== "all" && !days.includes(ibDay)) setIbDay("all"); }, [days, ibDay]);

  // Detect inside bars per the selected strategy's logic (timeframe + maxBabyPct +
  // minConsecutive), then map each back onto the displayed candles. Returns the set of
  // display-candle indices to tint and one marker per inside TF-bar (at its close).
  const { ibSet, ibMarks } = useMemo(() => {
    if (!ibStratId || !cs.length) return { ibSet: new Set(), ibMarks: [] };
    let tfMin = 1, maxBabyPct = 0, minConsecutive = 1;
    if (ibStratId !== "manual") {
      const sel = ibStrats.find((s) => s._id === ibStratId);
      // Non-inside-bar strategy (e.g. a deep-linked directional/scalp strategy):
      // no inside-bar pattern → no tint, just the overlay markers below.
      if (sel && !sel?.insideBar?.enabled) return { ibSet: new Set(), ibMarks: [] };
      const cfg = sel?.insideBar || {};
      tfMin = TF_MIN[cfg.timeframe] || 5;
      maxBabyPct = cfg.maxBabyPct || 0;
      minConsecutive = cfg.minConsecutive || 1;
    }
    const bars = resample(cs, tfMin);
    const flags = detectInside(bars, { maxBabyPct, minConsecutive });
    const set = new Set(); const marks = [];
    bars.forEach((b, i) => {
      if (!flags[i]) return;
      // Tint ONLY the inside bar's close candle (where the "IB" marker also sits), not every
      // underlying display candle. Otherwise a single inside TF-bar paints all its constituent
      // fine candles purple — so viewing a finer chart TF than the strategy's TF (e.g. a 1-min
      // chart with a 5-min inside-bar strategy) lit up a solid block of candles at once.
      const di = b._idx[b._idx.length - 1];
      set.add(di);
      marks.push({ di, date: b.date });
    });
    return { ibSet: set, ibMarks: marks };
  }, [cs, ibStratId, ibStrats]);

  const ibCount = useMemo(() => ibMarks.filter((m) => ibDay === "all" || m.date === ibDay).length, [ibMarks, ibDay]);

  // ── Strategy P&L overlay (real backtest: SL / target / exit + ₹ P&L) ──────────
  const [ov, setOv] = useState(null);        // overlay result { summary, trades }
  const [ovLoading, setOvLoading] = useState(false);
  const [ovErr, setOvErr] = useState(null);
  const [ovStratId, setOvStratId] = useState(""); // which strategy ov belongs to
  // Reset the overlay whenever the selected strategy changes (stale ≠ shown).
  useEffect(() => { if (ovStratId && ovStratId !== ibStratId) { setOv(null); setOvErr(null); } }, [ibStratId, ovStratId]);

  // (date,"HH:MM") → display-candle _t, so backend trade times map onto the chart.
  const tByKey = useMemo(() => { const m = new Map(); cs.forEach((c) => m.set(c.date + " " + c.time, c._t)); return m; }, [cs]);
  const tAt = (date, hhmm) => {
    if (!date || !hhmm) return null;
    const hit = tByKey.get(date + " " + hhmm); if (hit != null) return hit;
    const want = Date.parse(`${date}T${hhmm}:00Z`) / 1000;
    // Map the fill time onto the candle that CONTAINS it — the latest candle whose
    // start is at/before `want`. On a coarse TF (e.g. 60-min) a 09:16 entry sits
    // INSIDE the 09:15 candle, so the old "first candle at/after" logic wrongly
    // jumped to the next (10:15) bar. Falls back to the day's first candle if the
    // time precedes it.
    let best = null, first = null;
    for (const c of cs) {
      if (c.date !== date) continue;
      if (first == null) first = c._t;
      if (c._t <= want) best = c._t; else break; // cs ascending → past the time
    }
    return best != null ? best : first;
  };

  const runOverlay = useCallback(async () => {
    if (!ibStratId || ibStratId === "manual" || !cs.length) return;
    const from = cs[0].date, to = cs[cs.length - 1].date;
    setOvLoading(true); setOvErr(null);
    try {
      const r = await backtestAPI.overlay(ibStratId, from, to);
      setOv(r.data); setOvStratId(ibStratId);
    } catch (e) { setOv(null); setOvErr(e.response?.data?.message || e.message); }
    finally { setOvLoading(false); }
  }, [ibStratId, cs]);

  // Auto-run the overlay once for a deep-linked strategy when its candles are ready.
  const ovAutoRef = useRef(false);
  useEffect(() => {
    if (ovAutoRef.current || !deepLink?.strategyId || !cs.length) return;
    if (ibStratId !== deepLink.strategyId) return; // wait until the deep link applied
    ovAutoRef.current = true;
    runOverlay();
  }, [deepLink, cs, ibStratId, runOverlay]);

  // Trades to show right now, honoring the day filter.
  const ovTrades = useMemo(() => {
    if (!ov?.trades) return [];
    return ibDay === "all" ? ov.trades : ov.trades.filter((t) => t.date === ibDay);
  }, [ov, ibDay]);
  const ovDayPnl = useMemo(() => (ov && ibDay !== "all") ? ovTrades.reduce((s, t) => s + (t.pnl || 0), 0) : null, [ov, ibDay, ovTrades]);

  // Create the chart once.
  useEffect(() => {
    const el = elRef.current; if (!el) return;
    const chart = createChart(el, {
      width: el.clientWidth, height: 520,
      layout: { background: { color: "#fff" }, textColor: "#334155", fontSize: 12 },
      grid: { vertLines: { color: "#eef2f7" }, horzLines: { color: "#eef2f7" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: C.border, scaleMargins: { top: 0.08, bottom: 0.26 } },
      timeScale: { borderColor: C.border, timeVisible: true, secondsVisible: false, rightOffset: 6 },
      handleScroll: true, handleScale: true,
    });
    chartRef.current = chart;
    chart.subscribeCrosshairMove((p) => {
      if (!p.time || !p.seriesData || !mainRef.current) { setLegend(null); return; }
      const d = p.seriesData.get(mainRef.current);
      setLegend(d || null);
    });
    const ro = new ResizeObserver(() => chart.applyOptions({ width: el.clientWidth }));
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, []);

  // (Re)build all series whenever data / type / indicators / volume change.
  useEffect(() => {
    const chart = chartRef.current; if (!chart || !cs.length) return;
    const ts = chart.timeScale();
    const prevRange = ts.getVisibleLogicalRange();
    const fresh = prevDataRef.current !== candles;

    seriesRef.current.forEach((s) => chart.removeSeries(s));
    seriesRef.current = []; mainRef.current = null;

    const add = (s) => { seriesRef.current.push(s); return s; };
    const up = C.green, dn = C.red;
    // Tint candles/bars that belong to an inside bar (per the selected strategy), honoring
    // the day filter. lightweight-charts reads per-point color/borderColor/wickColor.
    const paint = (arr) => arr.map((d, i) =>
      (ibSet.has(i) && (ibDay === "all" || cs[i].date === ibDay))
        ? { ...d, color: IB, borderColor: IB, wickColor: IB } : d);

    // main price series
    let main;
    if (type === "line") { main = add(chart.addLineSeries({ color: C.blue, lineWidth: 2 })); main.setData(cs.map((c) => ({ time: c._t, value: c.c }))); }
    else if (type === "area") { main = add(chart.addAreaSeries({ lineColor: C.blue, topColor: "rgba(37,99,235,.25)", bottomColor: "rgba(37,99,235,.02)", lineWidth: 2 })); main.setData(cs.map((c) => ({ time: c._t, value: c.c }))); }
    else if (type === "bars") { main = add(chart.addBarSeries({ upColor: up, downColor: dn })); main.setData(paint(cs.map((c) => ({ time: c._t, open: c.o, high: c.h, low: c.l, close: c.c })))); }
    else { // candles / heikin
      main = add(chart.addCandlestickSeries({ upColor: up, downColor: dn, borderUpColor: up, borderDownColor: dn, wickUpColor: up, wickDownColor: dn }));
      main.setData(paint(type === "heikin" ? heikinAshi(cs) : cs.map((c) => ({ time: c._t, open: c.o, high: c.h, low: c.l, close: c.c }))));
    }
    mainRef.current = main;
    main.priceScale().applyOptions({ mode: log ? 1 : 0 });

    // Markers: inside-bar dots, plus per-trade entry/exit once a strategy's P&L overlay
    // has been run (entry arrow by breakout direction; exit square shows exitType + ₹).
    const markers = [];
    if (ibStratId && ibMarks.length) {
      ibMarks.filter((mk) => ibDay === "all" || mk.date === ibDay)
        .forEach((mk) => markers.push({ time: cs[mk.di]._t, position: "aboveBar", color: IB, shape: "circle", text: "IB" }));
    }
    if (ov && ovTrades.length) {
      // One entry + one exit marker PER leg-segment, so continuous candle re-entry
      // shows every re-entry (not just the first). Arrow direction by option type
      // (CE up/green, PE down/red); falls back to the inside-bar breakout dir.
      ovTrades.forEach((t) => {
        (t.legs || []).forEach((l) => {
          const ot = l.optType || t.ib?.dir;
          const et = tAt(t.date, l.entryTime);
          if (et != null) markers.push({ time: et, position: ot === "PE" ? "aboveBar" : "belowBar", color: ot === "PE" ? C.red : C.green, shape: ot === "PE" ? "arrowDown" : "arrowUp", text: l.reentry ? `Entry #${l.reentry + 1}` : "Entry" });
          const xt = tAt(t.date, l.exitTime);
          if (xt != null) markers.push({ time: xt, position: "aboveBar", color: (l.pnl || 0) >= 0 ? C.green : C.red, shape: "square", text: `${l.exitType || "EXIT"} ₹${Math.round(l.pnl || 0)}` });
        });
      });
    }
    if (markers.length) { markers.sort((a, b) => a.time - b.time); main.setMarkers(markers); }

    // Per-trade SL / target / breakout spot levels — drawn only when one day is in view
    // (price lines span the whole width, so all-days would be a thicket). Auto-removed
    // with the series on the next rebuild.
    if (ov && ibDay !== "all") {
      ovTrades.filter((t) => t.ib).forEach((t) => {
        const line = (price, color, title, style = 0) => { if (price != null) main.createPriceLine({ price, color, lineWidth: 1, lineStyle: style, axisLabelVisible: true, title }); };
        line(t.ib.breakoutSpot, "#2563eb", "Entry", 2);
        line(t.ib.slSpot, C.red, "SL");
        line(t.ib.targetSpot, C.green, "Target");
      });
    }

    // volume (overlay, bottom)
    if (showVol) {
      const vol = add(chart.addHistogramSeries({ priceScaleId: "vol", priceFormat: { type: "volume" }, color: "#cbd5e1" }));
      vol.priceScale().applyOptions({ scaleMargins: { top: 0.84, bottom: 0 } });
      vol.setData(cs.map((c) => ({ time: c._t, value: c.volume || 0, color: c.c >= c.o ? "rgba(16,185,129,.5)" : "rgba(239,68,68,.5)" })));
    }
    // indicators (price pane overlays)
    if (ind.ema9) { const s = add(chart.addLineSeries({ color: "#f59e0b", lineWidth: 1, priceLineVisible: false, crosshairMarkerVisible: false })); s.setData(emaLine(cs, 9)); }
    if (ind.ema21) { const s = add(chart.addLineSeries({ color: "#8b5cf6", lineWidth: 1, priceLineVisible: false, crosshairMarkerVisible: false })); s.setData(emaLine(cs, 21)); }
    if (ind.vwap) { const s = add(chart.addLineSeries({ color: "#0ea5e9", lineWidth: 1, lineStyle: 2, priceLineVisible: false, crosshairMarkerVisible: false })); s.setData(vwapLine(cs)); }

    if (fresh) {
      // Zoom to the LAST trading day so candles fill the area (not 2-3 days squeezed).
      const lastDate = cs[cs.length - 1].date;
      let startIdx = cs.length - 1;
      while (startIdx > 0 && cs[startIdx - 1].date === lastDate) startIdx--;
      ts.setVisibleLogicalRange({ from: startIdx - 1, to: cs.length + 3 });
    } else if (prevRange) ts.setVisibleLogicalRange(prevRange);
    prevDataRef.current = candles;
  }, [cs, candles, type, showVol, ind, log, ibSet, ibMarks, ibStratId, ibDay, ov, ovTrades]);

  // Zoom to the selected day when the inside-bar day filter changes.
  useEffect(() => {
    const chart = chartRef.current; if (!chart || ibDay === "all" || !cs.length) return;
    let from = -1, to = -1;
    cs.forEach((c, i) => { if (c.date === ibDay) { if (from < 0) from = i; to = i; } });
    if (from >= 0) chart.timeScale().setVisibleLogicalRange({ from: from - 1, to: to + 2 });
  }, [ibDay, cs]);

  const chip = (active) => ({ padding: "6px 12px", borderRadius: 7, border: `1px solid ${active ? C.blue : C.border}`, background: active ? C.blue : "#fff", color: active ? "#fff" : C.text, fontWeight: 600, fontSize: 13, cursor: "pointer" });
  const toggleInd = (k) => setInd((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        {toolbarLeft}
        {toolbarLeft && <span style={{ width: 1, height: 22, background: C.border }} />}
        <div style={{ display: "flex", gap: 4 }}>
          {TYPES.map((t) => <button key={t.id} onClick={() => setType(t.id)} style={chip(type === t.id)}>{t.label}</button>)}
        </div>
        <span style={{ width: 1, height: 22, background: C.border }} />
        <button onClick={() => toggleInd("ema9")} style={chip(ind.ema9)}>EMA 9</button>
        <button onClick={() => toggleInd("ema21")} style={chip(ind.ema21)}>EMA 21</button>
        <button onClick={() => toggleInd("vwap")} style={chip(ind.vwap)}>VWAP</button>
        <button onClick={() => setShowVol((v) => !v)} style={chip(showVol)}>Volume</button>
        <span style={{ width: 1, height: 22, background: C.border }} />
        <button onClick={() => setLog((v) => !v)} style={chip(log)}>Log</button>
        <button onClick={() => chartRef.current?.timeScale().fitContent()} style={chip(false)}>Fit</button>
        <span style={{ fontSize: 11, color: C.sub, marginLeft: "auto" }}>scroll = zoom · drag = pan</span>
      </div>

      {/* Inside-bar strategy overlay — pick a saved inside-bar strategy and every inside bar
          its logic forms (on its timeframe) is highlighted; filter to one day if needed. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10, padding: "8px 10px", border: `1px solid ${ibStratId ? IB : C.border}`, borderRadius: 9, background: ibStratId ? "rgba(124,58,237,.05)" : "#fbfcfe" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: ibStratId ? IB : C.text }}>📦 Inside Bars</span>
        <select value={ibStratId} onChange={(e) => { setIbStratId(e.target.value); setIbDay("all"); }} style={sel({ padding: "6px 8px", fontSize: 13, borderColor: ibStratId ? IB : "#e8edf3" })}>
          <option value="">Off — pick a strategy</option>
          <option value="manual">Manual (1:1, current chart TF)</option>
          {ibStrats.map((s) => (
            <option key={s._id} value={s._id}>{(s.name || "Strategy")} · {s.insideBar?.timeframe || "5min"}</option>
          ))}
        </select>
        {ibStratId && (
          <>
            <select value={ibDay} onChange={(e) => setIbDay(e.target.value)} style={sel({ padding: "6px 8px", fontSize: 13 })}>
              <option value="all">All days</option>
              {days.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <span style={{ fontSize: 13, fontWeight: 700, color: IB }}>{ibCount} inside bar{ibCount === 1 ? "" : "s"}</span>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: IB, display: "inline-block" }} />
          </>
        )}
        {ibStratId && ibStratId !== "manual" && (
          <>
            <span style={{ width: 1, height: 22, background: C.border }} />
            <button onClick={runOverlay} disabled={ovLoading} style={{ ...chip(false), borderColor: C.green, color: ovLoading ? C.sub : C.green, fontWeight: 700 }}>
              {ovLoading ? "Running…" : (ov ? "↻ Re-run P&L" : "▶ Run P&L")}
            </button>
            {ov && (
              <span style={{ fontSize: 13, fontWeight: 700, color: (ovDayPnl != null ? ovDayPnl : ov.summary.pnl) >= 0 ? C.green : C.red }}>
                {ovDayPnl != null ? `Day ₹${Math.round(ovDayPnl).toLocaleString("en-IN")}` : `Total ₹${ov.summary.pnl.toLocaleString("en-IN")}`}
              </span>
            )}
            {ovErr && <span style={{ fontSize: 12, color: C.red }}>{ovErr}</span>}
          </>
        )}
        {ibStratId && ibStratId !== "manual" && ibStrats.length === 0 && (
          <span style={{ fontSize: 12, color: C.sub }}>No inside-bar strategies found.</span>
        )}
        {!ibStrats.length && !ibStratId && (
          <span style={{ fontSize: 12, color: C.sub }}>Create an inside-bar strategy in Strategy Builder to use it here.</span>
        )}
      </div>

      {/* Real-backtest P&L summary for the selected strategy over the loaded range. */}
      {ov && ibStratId !== "manual" && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 10, padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 9, background: "#f8fafc", fontSize: 13 }}>
          <span style={{ fontWeight: 700 }}>P&L ({ov.from} → {ov.to}):</span>
          <span>Total <b style={{ color: ov.summary.pnl >= 0 ? C.green : C.red }}>₹{ov.summary.pnl.toLocaleString("en-IN")}</b></span>
          <span>Trades <b>{ov.summary.trades}</b></span>
          <span>Win <b>{ov.summary.winRate}%</b> ({ov.summary.wins}/{ov.summary.trades})</span>
          <span>Max profit <b style={{ color: C.green }}>₹{Math.round(ov.summary.maxProfit).toLocaleString("en-IN")}</b></span>
          <span>Max loss <b style={{ color: C.red }}>₹{Math.round(ov.summary.maxLoss).toLocaleString("en-IN")}</b></span>
          {ibDay !== "all" && ovTrades.length > 0 && (
            <span style={{ marginLeft: "auto", color: C.sub }}>
              {ibDay}: {ovTrades.map((t) => `${t.ib?.dir || ""} ${(t.legs?.[0]?.exitType || "")} ₹${Math.round(t.pnl)}`).join(" · ")}
            </span>
          )}
          <span style={{ fontSize: 11, color: C.sub, width: "100%" }}>{ov.summary.dataMode} · SL/Target lines show when a single day is selected. ₹ P&L is option-premium (real backtest); chart lines are spot levels.</span>
        </div>
      )}
      <div style={{ position: "relative" }}>
        {legend && (
          <div style={{ position: "absolute", top: 8, left: 10, zIndex: 3, display: "flex", gap: 12, fontSize: 12, background: "rgba(255,255,255,.9)", padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}` }}>
            {legend.open != null ? (
              <>
                <span>O <b>{legend.open.toFixed(2)}</b></span>
                <span style={{ color: C.green }}>H <b>{legend.high.toFixed(2)}</b></span>
                <span style={{ color: C.red }}>L <b>{legend.low.toFixed(2)}</b></span>
                <span>C <b>{legend.close.toFixed(2)}</b></span>
              </>
            ) : <span>Value <b>{(legend.value ?? 0).toFixed(2)}</b></span>}
          </div>
        )}
        <div ref={elRef} style={{ width: "100%", height: 520, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }} />
      </div>
    </div>
  );
}

export default function OptionChart() {
  const [symbol, setSymbol] = useState("");
  const [interval, setIntervalTf] = useState("1minute");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  // Dropdown builder (choose by underlying → year → month → expiry → strike → CE/PE)
  const [underlying, setUnderlying] = useState("NIFTY");
  const [expiries, setExpiries] = useState([]);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");   // '01'..'12'
  const [expiry, setExpiry] = useState("");
  const [strikes, setStrikes] = useState([]);
  const [strike, setStrike] = useState("");
  const [optType, setOptType] = useState("CE");
  // Smart symbol search (free-text → suggestions)
  const [suggest, setSuggest] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const sugTimer = useRef(null);
  // A search-picked expiry waiting to be honored by the [underlying] effect (instead
  // of it defaulting to the latest expiry) when a pick changes the underlying.
  const pendingExpiryRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false); // collapsible chart-toolbar search
  const searchRef = useRef(null);
  const colRef = useRef(null);          // chart column — measured so the strike panel matches its height
  const [colH, setColH] = useState(0);
  useEffect(() => {
    const el = colRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setColH(el.offsetHeight));
    ro.observe(el); setColH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  // Index / spot mode
  const isoDate = (d) => d.toISOString().slice(0, 10);
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); };
  const [mode, setMode] = useState("option"); // 'option' | 'index'
  const [idxUnderlying, setIdxUnderlying] = useState("NIFTY");
  const [idxFrom, setIdxFrom] = useState(daysAgo(10));
  const [idxTo, setIdxTo] = useState(isoDate(new Date()));

  // Deep link from a backtest "View chart" link: ?symbol=&underlying=&date=&tf=&strategyId=.
  // When the leg carries a real option `symbol`, open THAT strike's option chart focused on
  // the trade day. Otherwise (equity/crypto) fall back to the INDEX (spot) chart for the day.
  // <Chart> overlays the strategy's entry/exit (and SL/Target / inside-bar where applicable).
  const [sp] = useSearchParams();
  const dlStrategyId = sp.get("strategyId") || "";
  const dlDate = sp.get("date") || "";
  const dlUnd = (sp.get("underlying") || "").toUpperCase();
  const dlTf = sp.get("tf") || "";
  const dlSymbol = (sp.get("symbol") || "").toUpperCase();
  const dlOnceRef = useRef(false);
  // While a deep link governs the initial view, the underlying→latest-expiry→ATM auto-load
  // chain must NOT snap to the current month and overwrite the deep-linked chart. Seeded
  // true whenever a deep link is present; cleared the first time that chain would have run.
  const dlGovernRef = useRef(!!dlDate);
  // Strategy timeframe → chart interval, so a per-leg "View chart" opens on the SAME
  // timeframe the strategy runs. The index/option endpoints resample 1-min to any of
  // 1/3/5/15/30/60-min; anything else (or blank) falls back to 5-min.
  const tfToIdxInterval = (tf) => {
    const s = String(tf || "").toLowerCase().replace(/\s+/g, "");
    const map = {
      minute: "1minute", "1min": "1minute", "1m": "1minute",
      "3min": "3minute", "3m": "3minute",
      "5min": "5minute", "5m": "5minute",
      "10min": "15minute", "10minute": "15minute", // 10-min not served → nearest
      "15min": "15minute", "15m": "15minute",
      "30min": "30minute", "30m": "30minute",
      "60min": "60minute", "60m": "60minute", "1hour": "60minute", "1h": "60minute", hour: "60minute",
    };
    if (TFS.some((t) => t.id === s)) return s; // already a canonical id (e.g. "60minute")
    return map[s] || "5minute";
  };

  const load = useCallback(async (sym, tf) => {
    const s = String(sym || "").trim().toUpperCase();
    if (!s) { setMsg({ type: "err", text: "Enter an option symbol, e.g. NIFTY2661623200CE" }); return; }
    setLoading(true); setMsg(null);
    try {
      const res = await optionChartAPI.get(s, tf);
      setData(res.data);
      if (!res.data.candles?.length) setMsg({ type: "warn", text: "No candles returned." });
    } catch (e) {
      setData(null);
      setMsg({ type: "err", text: e.response?.data?.message || e.message });
    } finally { setLoading(false); }
  }, []);

  // Load the underlying INDEX (spot) candles for the chosen range.
  const loadIndex = useCallback(async (tf, from, to, und) => {
    setLoading(true); setMsg(null);
    try {
      const res = await optionChartAPI.index(und, tf, from, to);
      setData(res.data);
      if (!res.data.candles?.length) setMsg({ type: "warn", text: "No index data in this range." });
    } catch (e) { setData(null); setMsg({ type: "err", text: e.response?.data?.message || e.message }); }
    finally { setLoading(false); }
  }, []);

  const pickTf = (tf) => { setIntervalTf(tf); if (mode === "index") loadIndex(tf, idxFrom, idxTo, idxUnderlying); else load(data?.meta?.symbol || symbol, tf); };

  // Apply the deep link once. Needs at least a day to anchor on.
  //  • Leg with a real option symbol → OPTION mode: load that exact strike's candle life
  //    and let <Chart> focus the trade day (ibDay). This is the leg's own chart.
  //  • Otherwise → INDEX (spot) mode for the day (equity/crypto/legacy links).
  // <Chart> then auto-runs the strategy overlay (entry/exit markers) for the focused day.
  useEffect(() => {
    if (dlOnceRef.current || !dlDate) return;
    const tf = tfToIdxInterval(dlTf);
    // Option-symbol deep link → open the leg's own strike chart, and sync the
    // underlying/expiry/strike/CE-PE selectors to it (mirrors the search-pick flow) so the
    // [underlying]/[expiry] effects honor THIS contract instead of snapping to the latest.
    const p = dlSymbol && parseOptionSymbol(dlSymbol);
    if (p) {
      dlOnceRef.current = true;
      setMode("option"); setSymbol(""); setIntervalTf(tf);
      pendingExpiryRef.current = p.expiry;          // honored by the [underlying] effect
      setUnderlying(p.underlying);
      setYear(p.expiry.slice(0, 4)); setMonth(p.expiry.slice(5, 7)); setExpiry(p.expiry);
      setOptType(p.optType); setStrike(String(p.strike));
      load(p.symbol, tf);
      return;
    }
    // Index fallback needs a strategy to overlay; without one there's nothing to show.
    if (!dlStrategyId) return;
    dlOnceRef.current = true;
    const und = UNDERLYINGS.includes(dlUnd) ? dlUnd : "NIFTY";
    // Widen the range so the day's intraday candles are fully covered (a single
    // from==to slices to 00:00 and returns nothing): [date-1, date+1]. <Chart>
    // focuses the actual day via ibDay = date.
    const shift = (ymd, days) => { const [Y, M, D] = ymd.split("-").map(Number); return new Date(Date.UTC(Y, M - 1, D + days)).toISOString().slice(0, 10); };
    const from = shift(dlDate, -1), to = shift(dlDate, 1);
    setMode("index"); setIdxUnderlying(und); setIntervalTf(tf);
    setIdxFrom(from); setIdxTo(to);
    loadIndex(tf, from, to, und);
  }, [dlDate, dlSymbol, dlStrategyId, dlUnd, dlTf, load, loadIndex]);

  // Load available expiries when the underlying changes; default to the latest.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await optionChartAPI.expiries(underlying);
        if (!alive) return;
        const ex = r.data.expiries || [];
        setExpiries(ex);
        const pend = pendingExpiryRef.current;
        if (pend && ex.includes(pend)) {
          // A search pick / deep link picked this expiry — honor it (and keep the picked
          // strike) instead of snapping to the latest expiry + ATM.
          pendingExpiryRef.current = null;
          dlGovernRef.current = false;
          setYear(pend.slice(0, 4)); setMonth(pend.slice(5, 7)); setExpiry(pend);
        } else if (dlGovernRef.current) {
          // A deep link set the contract explicitly (its expiry just isn't downloaded as a
          // distinct entry, or the underlying differs) — leave the selection to the deep-link
          // branch; never snap to the current month and overwrite the opened chart.
          dlGovernRef.current = false;
        } else if (ex.length) {
          setStrike("");
          const last = ex[ex.length - 1];
          setYear(last.slice(0, 4)); setMonth(last.slice(5, 7)); setExpiry(last);
        } else { setStrike(""); setYear(""); setMonth(""); setExpiry(""); }
      } catch { if (alive) setExpiries([]); }
    })();
    return () => { alive = false; };
  }, [underlying]);

  // Load strikes when the expiry changes.
  useEffect(() => {
    let alive = true;
    if (!expiry) { setStrikes([]); return; }
    (async () => {
      try {
        const r = await optionChartAPI.strikes(underlying, expiry);
        if (!alive) return;
        const arr = r.data.strikes || [];
        setStrikes(arr);
        // Honor an already-selected strike (e.g. from a search pick) if it exists for
        // this expiry+side — it's already charted, so just keep the selectors in sync
        // and let the surrounding strikes populate the list/dropdown.
        const cur = Number(strike);
        if (cur && arr.some((s) => s.strike === cur && (optType === "CE" ? s.ce : s.pe))) return;
        // Auto-select this expiry's ATM (keep the current CE/PE side if it has data, else CE) and chart it.
        if (arr.length) {
          const atm = r.data.atm;
          const has = (st, o) => arr.some((s) => s.strike === st && (o === "CE" ? s.ce : s.pe));
          const side = atm != null && has(atm, optType) ? optType : "CE";
          const avail = arr.filter((s) => (side === "CE" ? s.ce : s.pe)).map((s) => s.strike);
          const pick = atm != null && avail.length ? avail.reduce((b, s) => (Math.abs(s - atm) < Math.abs(b - atm) ? s : b), avail[0]) : avail[0];
          if (pick != null) { setStrike(String(pick)); setOptType(side); load(buildSym(underlying, expiry, pick, side), interval); }
        }
      } catch { if (alive) setStrikes([]); }
    })();
    return () => { alive = false; };
  }, [underlying, expiry]);

  const years = useMemo(() => [...new Set(expiries.map((e) => e.slice(0, 4)))].sort().reverse(), [expiries]);
  const months = useMemo(() => [...new Set(expiries.filter((e) => !year || e.slice(0, 4) === year).map((e) => e.slice(5, 7)))].sort(), [expiries, year]);
  const expiryOpts = useMemo(() => expiries.filter((e) => (!year || e.slice(0, 4) === year) && (!month || e.slice(5, 7) === month)).sort(), [expiries, year, month]);
  // Strikes available for the currently-selected CE/PE (so the list isn't a CE+PE mix).
  const strikeOpts = useMemo(() => strikes.filter((s) => (optType === "CE" ? s.ce : s.pe)).map((s) => s.strike), [strikes, optType]);
  const strikeList = useMemo(() => [...strikeOpts].sort((a, b) => b - a), [strikeOpts]); // high → low for the side panel
  const selRowRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    const row = selRowRef.current, cont = listRef.current;
    if (row && cont) cont.scrollTop = Math.max(0, row.offsetTop - cont.clientHeight / 2 + row.clientHeight / 2);
  }, [strike, strikeList]);

  // Build the symbol from current parts and load it.
  const chooseAndLoad = (st, opt, exp = expiry) => {
    const s = buildSym(underlying, exp, st, opt);
    if (s) load(s, interval); // load only; search box stays empty (shows current via chart meta)
  };

  // Free-text symbol search (debounced) — "16 jun 2026 23000" → suggestions.
  const onSymbolChange = (val) => {
    setSymbol(val);
    if (sugTimer.current) clearTimeout(sugTimer.current);
    const q = val.trim();
    if (q.length < 2) { setSuggest([]); setShowSug(false); return; }
    sugTimer.current = setTimeout(async () => {
      try { const r = await optionChartAPI.search(q, underlying); setSuggest(r.data.results || []); setShowSug(true); }
      catch { setSuggest([]); }
    }, 250);
  };
  // Pick a suggestion → chart it AND sync the dropdowns + strike panel to it, so the
  // selectors show the charted strike (with its neighbours) instead of the old defaults.
  const pickSuggest = (s) => {
    setSymbol(""); setShowSug(false); setSuggest([]); setSearchOpen(false);
    pendingExpiryRef.current = s.expiry;            // honored by the [underlying] effect if it changes
    setUnderlying(s.underlying);
    setYear(s.expiry.slice(0, 4)); setMonth(s.expiry.slice(5, 7)); setExpiry(s.expiry);
    setOptType(s.optType);
    setStrike(String(s.strike));
    load(s.symbol, interval);
  };

  const m = data?.meta;

  // Collapsible search for the chart toolbar (icon → smoothly expands to a small input).
  const searchEl = mode === "option" ? (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <button title="Search symbol"
        onClick={() => { const n = !searchOpen; setSearchOpen(n); if (n) setTimeout(() => searchRef.current?.focus(), 60); }}
        style={{ width: 32, height: 30, flex: "0 0 auto", borderRadius: 8, border: `1px solid ${searchOpen ? C.blue : C.border}`, background: "#fff", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
        🔍
      </button>
      <input ref={searchRef} value={symbol} onChange={(e) => onSymbolChange(e.target.value)}
        onFocus={() => suggest.length && setShowSug(true)}
        onBlur={() => setTimeout(() => { setShowSug(false); if (!symbol) setSearchOpen(false); }, 150)}
        onKeyDown={(e) => { if (e.key === "Enter") { setShowSug(false); if (suggest[0]) pickSuggest(suggest[0]); else load(symbol, interval); } if (e.key === "Escape") setSearchOpen(false); }}
        placeholder="16 jun 2026 23000 ce  or  NIFTY…"
        style={{ width: searchOpen ? 240 : 0, maxWidth: "100%", opacity: searchOpen ? 1 : 0, marginLeft: searchOpen ? 6 : 0,
          padding: searchOpen ? "7px 10px" : "7px 0", border: `1px solid ${searchOpen ? C.border : "transparent"}`,
          borderRadius: 8, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box", pointerEvents: searchOpen ? "auto" : "none",
          transition: "width .22s ease, opacity .18s ease, margin .22s ease, padding .22s ease" }} />
      {showSug && searchOpen && suggest.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 30, marginTop: 6, width: 330, maxWidth: "92vw", maxHeight: 300, overflowY: "auto", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
          {suggest.map((s) => (
            <div key={s.symbol} onMouseDown={() => pickSuggest(s)}
              style={{ padding: "8px 12px", cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 10, borderBottom: `1px solid ${C.bg}` }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)} onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}>
              <span style={{ fontFamily: "monospace", fontSize: 13, color: C.text }}>{s.symbol}</span>
              <span style={{ fontSize: 12, color: C.sub, whiteSpace: "nowrap" }}>{s.expiry} · {s.strike} <b style={{ color: s.optType === "CE" ? C.green : C.red }}>{s.optType}</b></span>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div>
      <div className="page-hero" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div className="hero-icon" style={{ width: 46, height: 46, borderRadius: 13, background: "linear-gradient(135deg,#2563eb,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23, boxShadow: "0 6px 16px rgba(37,99,235,.28)" }}>📉</div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-0.3px", color: C.text }}>Option Chart</h1>
      </div>

      <div style={card({ padding: 18, marginBottom: 18 })}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {[["option", "Option"], ["index", "Index / Spot"]].map(([id, lbl]) => (
            <button key={id} onClick={() => setMode(id)}
              style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${mode === id ? C.blue : C.border}`, background: mode === id ? C.blue : "#fff", color: mode === id ? "#fff" : C.text, fontWeight: 700, cursor: "pointer" }}>
              {lbl}
            </button>
          ))}
        </div>

        {mode === "index" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Index</label>
              <select value={idxUnderlying} onChange={(e) => setIdxUnderlying(e.target.value)} style={sel()}>
                {UNDERLYINGS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>From</label>
              <input type="date" value={idxFrom} onChange={(e) => setIdxFrom(e.target.value)} style={sel()} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>To</label>
              <input type="date" value={idxTo} onChange={(e) => setIdxTo(e.target.value)} style={sel()} />
            </div>
            <button onClick={() => loadIndex(interval, idxFrom, idxTo, idxUnderlying)} disabled={loading}
              style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: loading ? "#93c5fd" : C.blue, color: "#fff", fontWeight: 700, cursor: loading ? "default" : "pointer" }}>
              {loading ? "Loading…" : "Load Index"}
            </button>
          </div>
        )}

        {mode === "option" && (<>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))", gap: 10, marginBottom: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Underlying</label>
            <select value={underlying} onChange={(e) => { pendingExpiryRef.current = null; setUnderlying(e.target.value); }} style={sel({ width: "100%" })}>
              {UNDERLYINGS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Year</label>
            <select value={year} onChange={(e) => { setYear(e.target.value); setMonth(""); setExpiry(""); setStrike(""); }} style={sel({ width: "100%" })}>
              <option value="">All</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Month</label>
            <select value={month} onChange={(e) => { setMonth(e.target.value); setExpiry(""); setStrike(""); }} style={sel({ width: "100%" })}>
              <option value="">All</option>
              {months.map((mm) => <option key={mm} value={mm}>{MONTHS[Number(mm) - 1]}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Expiry</label>
            <select value={expiry} onChange={(e) => { setExpiry(e.target.value); setStrike(""); }} style={sel({ width: "100%" })}>
              <option value="">Select</option>
              {expiryOpts.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Strike</label>
            <select value={strike} onChange={(e) => { setStrike(e.target.value); chooseAndLoad(e.target.value, optType); }} style={sel({ width: "100%" })} disabled={!strikeOpts.length}>
              <option value="">{strikeOpts.length ? "Select" : "—"}</option>
              {strikeOpts.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: C.sub, marginBottom: 6 }}>Type</label>
            <div style={{ display: "flex", gap: 6 }}>
              {["CE", "PE"].map((o) => (
                <button key={o} onClick={() => {
                    const valid = strike && strikes.some((s) => s.strike === Number(strike) && (o === "CE" ? s.ce : s.pe));
                    setOptType(o);
                    if (valid) chooseAndLoad(strike, o); else setStrike("");
                  }}
                  style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1px solid ${optType === o ? C.blue : C.border}`, background: optType === o ? C.blue : "#fff", color: optType === o ? "#fff" : C.text, fontWeight: 700, cursor: "pointer" }}>
                  {o}
                </button>
              ))}
            </div>
          </div>
        </div>
        </>)}
      </div>

      {msg && (
        <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, fontSize: 14,
          background: msg.type === "err" ? "#fee2e2" : msg.type === "warn" ? "#fef9c3" : "#d1fae5",
          color: msg.type === "err" ? "#991b1b" : msg.type === "warn" ? "#854d0e" : "#065f46" }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div ref={colRef} style={{ flex: "1 1 380px", minWidth: 0, ...card({ padding: 18 }) }}>
        {/* Chart top bar — instrument meta (left) + timeframe (right). Search sits in
            the chart toolbar below as a collapsible icon (passed via toolbarLeft). */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "baseline", fontSize: 13, color: C.text }}>
            {m ? (m.isIndex ? (
              <span style={{ fontWeight: 700, fontFamily: "monospace" }}>{m.symbol} <span style={{ color: C.sub, fontWeight: 500 }}>Index · {String(m.from).slice(0, 10)} → {String(m.to).slice(0, 10)}</span></span>
            ) : (
              <>
                <span style={{ fontWeight: 700, fontFamily: "monospace" }}>{m.symbol}</span>
                <span style={{ color: C.sub }}>{m.expiry} · {m.strike} {m.optType}</span>
                <span style={{ color: C.sub }}>{m.count} candles{m.lotSize ? ` · lot ${m.lotSize}` : ""}</span>
              </>
            )) : <span style={{ color: C.sub }}>{mode === "index" ? `${idxUnderlying} Index` : "—"}</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {TFS.map((t) => (
              <button key={t.id} onClick={() => pickTf(t.id)}
                style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${interval === t.id ? C.blue : C.border}`, background: interval === t.id ? C.blue : "#fff", color: interval === t.id ? "#fff" : C.text, fontWeight: 600, cursor: "pointer" }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {m && data?.candles?.length > 0 ? (
          <Chart candles={data.candles} toolbarLeft={searchEl} deepLink={dlDate ? { strategyId: dlStrategyId, date: dlDate } : null} />
        ) : (
          <div style={{ height: 360, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: C.sub, fontSize: 14, border: `1px dashed ${C.border}`, borderRadius: 10, background: C.bg }}>
            {loading ? "Loading…" : (mode === "option" ? "Search a symbol or pick from the dropdowns above to load the chart." : "Pick an index range above and Load.")}
          </div>
        )}
        </div>

        {mode === "option" && strikeList.length > 0 && (
          <div style={{ flex: "0 0 23%", minWidth: 190, maxWidth: 280, display: "flex", flexDirection: "column", ...card({ padding: 0 }), overflow: "hidden", height: colH || undefined, maxHeight: colH || 620 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>Strikes</span>
              <div style={{ display: "flex", gap: 4 }}>
                {["CE", "PE"].map((o) => (
                  <button key={o} onClick={() => {
                      const valid = strike && strikes.some((s) => s.strike === Number(strike) && (o === "CE" ? s.ce : s.pe));
                      setOptType(o);
                      if (valid) chooseAndLoad(strike, o); else setStrike("");
                    }}
                    style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${optType === o ? C.blue : C.border}`, background: optType === o ? C.blue : "#fff", color: optType === o ? "#fff" : C.text, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
            <div ref={listRef} style={{ overflowY: "auto", flex: 1, minHeight: 0, position: "relative" }}>
              {strikeList.map((st) => {
                const isSel = Number(strike) === st;
                return (
                  <div key={st} ref={isSel ? selRowRef : null}
                    onClick={() => { setStrike(String(st)); chooseAndLoad(st, optType); }}
                    style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, fontFamily: "monospace", fontWeight: isSel ? 700 : 500,
                      color: isSel ? "#fff" : C.text, background: isSel ? C.blue : "#fff", borderBottom: `1px solid ${C.bg}`,
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = C.bg; }}
                    onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "#fff"; }}>
                    <span>{st}</span>
                    <span style={{ fontSize: 11, color: isSel ? "rgba(255,255,255,.85)" : C.sub }}>{optType}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
